import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import CFSelect from "../shared/forms/CFSelect";
import useIntegrationRefresh from "../integrations/useIntegrationRefresh";
import FormulaInput from "./FormulaInput";
import { buildTypedFromParent } from "../../utils/integrationFieldTypeMap";
import { VariantContextData } from "../variants/VariantBuilderContext";
import FieldPickerPopover, {
  badgeToPromptToken,
} from "../shared/FieldPickerPopover";
import mergeTagUpgradeGate from "../../utils/mergeTagUpgradeGate";

const TEXT_COMMIT_DEBOUNCE_MS = 400;

const ActionSettingsInput = (props) => {
  const [selectSettingValue, setSelectSettingValue] = useState(undefined);
  const [typedMultiSelected, setTypedMultiSelected] = useState(undefined);
  const { isIntegrationRefreshing } = useIntegrationRefresh();
  const inputRef = useRef(null);
  const commitTimerRef = useRef(null);
  const contextValue = useContext(VariantContextData);
  const contextData = contextValue?.[0];

  const setting = props.setting;
  const integration = props.integration;
  const website = props.website;
  const integrationLoading = isIntegrationRefreshing(
    integration && integration.id
  );

  let setting_value;
  let value_paths = setting.attribute
    .replace(/\]\[/g, ".")
    .replace(/\[/g, "")
    .replace(/\]/g, "")
    .split(".");

  value_paths.forEach((path) => {
    if (setting_value) {
      setting_value = setting_value[path];
    } else if (props.action) {
      setting_value =
        props.action[path] || (props.action.data && props.action.data[path]);
    }
  });

  const valueForAttribute = (attribute) => {
    let resolvedValue;
    const paths = attribute
      .replace(/\]\[/g, ".")
      .replace(/\[/g, "")
      .replace(/\]/g, "")
      .split(".");

    paths.forEach((path) => {
      if (resolvedValue !== undefined && resolvedValue !== null) {
        resolvedValue = resolvedValue[path];
      } else if (props.formData) {
        resolvedValue = props.formData[path];
      } else if (props.action) {
        resolvedValue =
          props.action[path] || (props.action.data && props.action.data[path]);
      }
    });

    return resolvedValue;
  };

  const [inputValue, setInputValue] = useState(
    setting_value ?? setting.value ?? ""
  );

  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) return;
    setInputValue(setting_value ?? "");
  }, [setting_value]);

  const settingSelectOptions = () => {
    let selectOptions = [
      {
        value: "",
        label: setting.placeholder,
        isDisabled: true,
      },
    ];

    if (
      !integration &&
      setting.dropdown == "fields" &&
      props.builder !== false
    ) {
      selectOptions.push({
        value: "CURRENT_SCORE",
        label: "Current score",
      });
    }

    if (setting.options) {
      setting.options.forEach((option) => {
        selectOptions.push({
          value: option.value,
          label: option.label,
        });
      });
    }

    if (integration && integration.api_data[setting.dropdown]) {
      Object.values(integration.api_data[setting.dropdown]).forEach(
        (option) => {
          selectOptions.push({
            value: option.id,
            label: option.name,
          });
        }
      );
    }

    if (
      integration &&
      integration.integration_type == "Shopify" &&
      setting.dropdown == "product_variants" &&
      (!integration.api_data[setting.dropdown] ||
        Object.values(integration.api_data[setting.dropdown]).length == 0)
    ) {
      const populateProducts = (products) => {
        const productsVariants = products
          .map((product) => product.variants)
          .flat();
        productsVariants.forEach((variant) => {
          selectOptions.push({
            value: variant.id,
            label: variant.variant_title,
          });
        });
      };

      if (!window.cf_shopify_products) {
        new ProductsIntegrationsService("Shopify", integration)
          .adapter()
          .getProducts()
          .then((response) => {
            window.cf_shopify_products = response;
            populateProducts(response);
          });
      } else {
        populateProducts(window.cf_shopify_products);
      }
    }

    if (
      integration &&
      integration.integration_type == "Shopify" &&
      setting.dropdown == "selling_plans" &&
      props.parent_value
    ) {
      if (!window.cf_shopify_products) {
        new ProductsIntegrationsService("Shopify", integration)
          .adapter()
          .getProducts()
          .then((response) => {
            window.cf_shopify_products = response;
          });
      } else {
        const selectedProductVariant = window.cf_shopify_products
          .map((product) => product.variants)
          .flat()
          .find((variant) => variant.id == props.parent_value.value);
        const selectedProduct = window.cf_shopify_products.find(
          (product) => product.id == selectedProductVariant?.product_id
        );
        const productSellingPlans =
          selectedProduct?.subscriptions
            ?.map((plan) => plan.sellingPlans)
            .flat() || [];

        if (
          selectedProduct &&
          (selectedProduct.requires_selling_plan !== true ||
            productSellingPlans?.length == 0) &&
          setting.hide_if_empty !== true
        ) {
          selectOptions.push({
            value: "",
            label: "One-time purchase",
          });

          if (!selectSettingValue) {
            setSelectSettingValue({
              value: "",
              label: "One-time purchase",
            });
          }
        }

        productSellingPlans.forEach((plan) => {
          selectOptions.push({
            value: plan.id.replace("gid://shopify/SellingPlan/", ""),
            label: plan.name,
          });
        });
      }
    }

    {
      !integration &&
        setting.dropdown == "fields" &&
        Object.values(website.fields)
          .filter((f) => !f.service || f.service == "ConvertFlow")
          .forEach((field) => {
            selectOptions.push({
              value: field.slug,
              label: field.slug,
            });
          });
    }

    {
      !integration &&
        setting.dropdown == "tags" &&
        Object.values(website.tags).forEach((tag) => {
          selectOptions.push({
            value: tag.id,
            label: tag.name,
          });
        });
    }

    {
      !integration &&
        setting.dropdown == "ctas" &&
        Object.values(website.ctas).forEach((cta) => {
          selectOptions.push({
            value: cta.id,
            label: cta.name,
          });
        });
    }

    if (!integration && setting.dropdown == "ctas") {
      selectOptions.push({
        value: null,
        label: `This campaign (${props.cta.name})`,
      });
      Object.values(website.ctas).forEach((cta) => {
        if (cta.id !== props.cta.id) {
          selectOptions.push({
            value: cta.id,
            label: `${cta.name} (${cta.cta_type})`,
          });
        }
      });
    }

    {
      !integration &&
        setting.dropdown == "webhooks" &&
        Object.values(website.webhooks).forEach((webhook) => {
          selectOptions.push({
            value: webhook.id,
            label: webhook.name ? webhook.name : webhook.target_url,
          });
        });
    }

    {
      !integration &&
        setting.options &&
        setting.options
          .filter((option) => {
            return (
              option &&
              option.text &&
              (!option.parent || option.parent == props.parent_value?.value)
            );
          })
          .sort(function (a, b) {
            return a.text.localeCompare(b.text);
          })
          .forEach((dropdown_option, i) => {
            var custom_attributes = {};

            dropdown_option.custom_attributes &&
              dropdown_option.custom_attributes.forEach((custom_attribute) => {
                custom_attributes[custom_attribute.name] =
                  custom_attribute.value;
              });

            selectOptions.push({
              value: dropdown_option.value,
              label: dropdown_option.text,
              ...custom_attributes,
            });
          });
    }

    return selectOptions;
  };

  const clearCommitTimer = () => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
  };

  const commitText = (value) => {
    if (props.onDone) {
      props.onDone({ target: { value } });
    }
  };

  const handleTextBlur = (e) => {
    clearCommitTimer();
    commitText(e.target?.value);
  };

  const changeCallback = (e) => {
    if (setting.input_type == "select") {
      setSelectSettingValue({ label: e.label, value: e.value });
    } else {
      setInputValue(e.target?.value);
    }

    if (props.onChange) {
      props.onChange(e);
    }

    if (props.onDone && setting.input_type == "select") {
      if (
        setting.child_setting &&
        setting.child_setting.reset_on_parent_change == true
      ) {
        updateFormData(setting.child_setting.attribute, "");
      }

      props.onDone(e);
    } else if (
      setting.input_type !== "select" &&
      setting.input_type !== "formula"
    ) {
      // Text/number: debounce the commit so it lands once typing pauses.
      const value = e.target?.value;
      clearCommitTimer();
      commitTimerRef.current = setTimeout(() => {
        commitTimerRef.current = null;
        commitText(value);
      }, TEXT_COMMIT_DEBOUNCE_MS);
    }

    if (setting.callback) {
      setting.callback(e);
    }
  };

  // Clear any pending debounced commit on unmount.
  useEffect(() => clearCommitTimer, []);

  const insertPromptField = (badge) => {
    if (!mergeTagUpgradeGate(badge, website || contextData?.objects?.website)) {
      return;
    }

    const token = badgeToPromptToken(badge);
    if (!token) return;

    const el = inputRef.current;
    const currentValue = inputValue ?? "";
    let nextValue = currentValue + token;
    let cursor = nextValue.length;

    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      nextValue =
        currentValue.substring(0, start) + token + currentValue.substring(end);
      cursor = start + token.length;
    }

    setInputValue(nextValue);
    clearCommitTimer();
    commitText(nextValue);
    if (props.onChange) {
      props.onChange({ target: { value: nextValue } });
    }

    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(cursor, cursor);
      }
    });
  };

  const updateFormData = (attribute, value) => {
    let newFormData = props.formData;
    _.set(
      newFormData,
      `${!integration ? attribute : `[data][${attribute}]`}`,
      value
    );
    props.setFormData(newFormData);
  };

  const handleFormulaBuilderSave = ({ formula, field, roundTo, commit }) => {
    const normalizedFormula = (formula || "").trim();
    const normalizedField = field || "";
    const normalizedRoundTo =
      roundTo === undefined || roundTo === null ? "" : `${roundTo}`.trim();

    updateFormData(setting.attribute, normalizedFormula);
    updateFormData("[data][field]", normalizedField);
    updateFormData("[data][round_to]", normalizedRoundTo);

    if (commit && props.onFormulaCommit) {
      props.onFormulaCommit(props.formData);
    }

    if (props.onChange) {
      props.onChange({ target: { value: normalizedFormula } });
    }

    if (!props.action) return;

    if (props.builder) {
      const actionUpdate = (setting_name, value) => ({
        object_type: "actions",
        object_id: props.action.id,
        setting_name,
        value,
      });
      dispatchCustomEvent("updateBuilder", {
        updates: [
          actionUpdate(setting.attribute, normalizedFormula),
          actionUpdate("[data][field]", normalizedField),
          actionUpdate("[data][round_to]", normalizedRoundTo),
        ],
      });
      return;
    }

    if (props.onDone) {
      props.onDone({ target: { value: normalizedFormula } });
    }
  };

  useEffect(() => {
    let selectOption = settingSelectOptions().find(
      (option) => option.value == setting_value
    );

    if (selectOption || setting_value) {
      setSelectSettingValue(
        selectOption || {
          label: setting_value,
          value: setting_value,
        }
      );
    }
  }, [setting_value, window?.cf_shopify_products]);

  const parentProperty = useMemo(() => {
    if (!setting.typed_from_parent) return null;
    const properties =
      integration &&
      integration.api_data &&
      (integration.api_data.properties || integration.api_data.fields);
    const parentVal = props.parent_value && props.parent_value.value;
    if (!properties || !parentVal) return null;
    return Object.values(properties).find(
      (p) => String(p.id) === String(parentVal)
    );
  }, [
    setting.typed_from_parent,
    integration,
    props.parent_value && props.parent_value.value,
  ]);

  const typedFromParent = useMemo(
    () => buildTypedFromParent(parentProperty, setting_value),
    [parentProperty, setting_value]
  );

  const typedSingleValue = useMemo(() => {
    if (!typedFromParent || typedFromParent.kind !== "select") return undefined;
    if (
      setting_value === undefined ||
      setting_value === null ||
      setting_value === ""
    )
      return undefined;
    return (
      typedFromParent.options.find(
        (o) => String(o.value) === String(setting_value)
      ) || {
        value: setting_value,
        label: setting_value,
      }
    );
  }, [typedFromParent, setting_value]);

  const typedMultiValue = useMemo(() => {
    if (!typedFromParent || typedFromParent.kind !== "multi_select") return [];
    if (!setting_value) return [];
    return String(setting_value)
      .split(", ")
      .filter((v) => v.length > 0)
      .map(
        (v) =>
          typedFromParent.options.find((o) => o.value === v) || {
            value: v,
            label: v,
          }
      );
  }, [typedFromParent, setting_value]);

  const typedSelectChange = (selected) => {
    const event = { label: selected.label, value: selected.value };
    setSelectSettingValue(event);
    if (props.onChange) props.onChange(event);
    if (props.onDone) props.onDone(event);
  };

  const typedMultiChange = (selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    const stringValue = arr.map((o) => o.value).join(", ");
    const event = { value: stringValue, label: stringValue };
    setTypedMultiSelected(arr);
    setInputValue(stringValue);
    if (props.onChange) props.onChange(event);
    if (props.onDone) props.onDone(event);
  };

  const settingTitle = (() => {
    if (Array.isArray(selectSettingValue)) {
      return selectSettingValue
        .map((option) => option.label || option.value)
        .filter(Boolean)
        .join(", ");
    }
    if (selectSettingValue && selectSettingValue.label) {
      return selectSettingValue.label;
    }
    if (selectSettingValue && selectSettingValue.value) {
      return String(selectSettingValue.value);
    }
    if (typedSingleValue) {
      return typedSingleValue.label || typedSingleValue.value || "";
    }
    if (Array.isArray(typedMultiValue) && typedMultiValue.length > 0) {
      return typedMultiValue
        .map((option) => option.label || option.value)
        .filter(Boolean)
        .join(", ");
    }
    if (inputValue != null && inputValue !== "") return String(inputValue);
    return setting_value == null ? "" : String(setting_value);
  })();

  return (
    <React.Fragment>
      {settingSelectOptions().length <= 1 && setting.hide_if_empty == true ? (
        ""
      ) : (
        <React.Fragment>
          <div
            className={`${
              props.hideLabel
                ? "w-full min-w-0"
                : `flex-grow ${
                    setting.input_type === "formula"
                      ? "min-w-0"
                      : "min-w-[100px]"
                  }`
            } ${setting.note ? "pb-[10px]" : ""}`}
            title={settingTitle || undefined}
          >
            {setting.label && !props.hideLabel && (
              <div
                className={`mb-[5px] flex items-center ${
                  setting.merge_tags ? "justify-between gap-3" : ""
                }`}
              >
                <label className="setting-label m-0 text-xl text-black dark:text-white capitalize">
                  {setting.label}
                </label>
                {setting.merge_tags && (
                  <FieldPickerPopover
                    className="relative"
                    website={website || contextData?.objects?.website}
                    variant={contextData?.objects?.variant}
                    onSelect={insertPromptField}
                    buttonClassName="px-2 py-1 rounded-lg border-none text-lg text-blue-600 dark:text-blue-400 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer whitespace-nowrap"
                    dropdownClassName="absolute right-0 top-full z-50 mt-1 w-[300px] max-h-[300px] overflow-y-auto bg-white dark:bg-slate-950 rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 shadow-lg"
                  />
                )}
              </div>
            )}

            {typedFromParent && typedFromParent.kind === "select" && (
              <CFSelect
                className="smart-select"
                placeholder={setting.placeholder}
                isSearchable={true}
                menuPortalTarget={document.body}
                menuPortalZIndex={props.menuPortalZIndex}
                required="required"
                name={`action_object[data][${setting.attribute}]`}
                options={typedFromParent.options}
                value={typedSingleValue || selectSettingValue}
                onChange={typedSelectChange}
                isLoading={integrationLoading}
              />
            )}

            {typedFromParent && typedFromParent.kind === "multi_select" && (
              <CFSelect
                className="smart-select"
                placeholder={setting.placeholder}
                isSearchable={true}
                isMulti={true}
                menuPortalTarget={document.body}
                menuPortalZIndex={props.menuPortalZIndex}
                required="required"
                name={`action_object[data][${setting.attribute}]`}
                options={typedFromParent.options}
                value={typedMultiSelected || typedMultiValue}
                onChange={typedMultiChange}
                isLoading={integrationLoading}
              />
            )}

            {typedFromParent && typedFromParent.kind === "native" && (
              <input
                ref={inputRef}
                className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                placeholder={setting.placeholder}
                type={typedFromParent.inputType}
                name={`action_object[data][${setting.attribute}]`}
                required="required"
                min={setting.min}
                value={inputValue ?? ""}
                title={settingTitle || undefined}
                onChange={changeCallback}
                onBlur={handleTextBlur}
              ></input>
            )}

            {!typedFromParent && setting.input_type === "formula" && (
              <FormulaInput
                value={setting_value || ""}
                name={
                  integration
                    ? `action_object[data][${setting.attribute}]`
                    : `action_object${setting.attribute}`
                }
                placeholder={setting.placeholder}
                website={website}
                targetFieldValue={valueForAttribute("[data][field]")}
                roundToValue={valueForAttribute("[data][round_to]")}
                onChange={changeCallback}
                onBlur={props.onDone}
                onSaveSettings={handleFormulaBuilderSave}
              />
            )}

            {!typedFromParent && setting.input_type === "textarea" && (
              <textarea
                ref={inputRef}
                className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                placeholder={setting.placeholder}
                name={
                  integration
                    ? `action_object[data][${setting.attribute}]`
                    : `action_object${setting.attribute}`
                }
                required="required"
                rows={setting.rows || 3}
                value={inputValue ?? ""}
                title={settingTitle || undefined}
                onChange={changeCallback}
                onBlur={handleTextBlur}
              ></textarea>
            )}

            {!typedFromParent &&
              setting.input_type !== "select" &&
              setting.input_type !== "formula" &&
              setting.input_type !== "textarea" && (
                <input
                  ref={inputRef}
                  className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                  placeholder={setting.placeholder}
                  type={setting.input_type}
                  name={
                    integration
                      ? `action_object[data][${setting.attribute}]`
                      : `action_object${setting.attribute}`
                  }
                  required="required"
                  min={setting.min}
                  value={inputValue ?? ""}
                  title={settingTitle || undefined}
                  onChange={changeCallback}
                  onBlur={handleTextBlur}
                ></input>
              )}

            {!typedFromParent && setting.input_type == "select" && (
              <CFSelect
                key={`sel-${setting.attribute}-${
                  selectSettingValue ? selectSettingValue.value : ""
                }`}
                className="smart-select"
                placeholder={setting.placeholder}
                isSearchable={true}
                menuPortalTarget={document.body}
                menuPortalZIndex={props.menuPortalZIndex}
                resetIfInvalidOption={setting.reset_if_invalid_option}
                required="required"
                name={
                  integration
                    ? `action_object[data][${setting.attribute}]`
                    : `action_object${setting.attribute}`
                }
                isLoading={Boolean(props.isLoading) || integrationLoading}
                options={settingSelectOptions()}
                value={selectSettingValue}
                onChange={changeCallback}
              />
            )}
          </div>

          {setting.input_type == "select" &&
            setting.child_setting &&
            !props.dropdownOnly && (
              <ActionSettingsInput
                action={props.action}
                hideLabel={props.hideLabel}
                menuPortalZIndex={props.menuPortalZIndex}
                setting={setting.child_setting}
                integration={integration}
                setFormData={props.setFormData}
                parent_value={selectSettingValue || { value: setting_value }}
                onChange={(e) => {
                  updateFormData(
                    setting.child_setting.attribute,
                    e.value || e.target?.value
                  );

                  if (
                    props.action &&
                    !props.builder &&
                    setting.child_setting.input_type == "select"
                  ) {
                    props.handleSubmit(newFormData);
                  }
                }}
                onDone={(e) => {
                  if (props.action) {
                    if (props.builder) {
                      const value = e.value || e.target?.value;
                      dispatchCustomEvent("updateBuilder", {
                        updates: [
                          {
                            object_type: "actions",
                            object_id: props.action.id,
                            setting_name: !integration
                              ? setting.child_setting.attribute
                              : `[data][${setting.child_setting.attribute}]`,
                            value: value,
                          },
                        ],
                      });
                    } else if (setting.child_setting.input_type !== "select") {
                      props.handleSubmit();
                    }
                  }
                }}
              />
            )}

          {setting.note && (
            <div className="text-black dark:text-white whitespace-nowrap text-sm absolute -bottom-[6px] max-w-[566px]">
              {setting.note}
            </div>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ActionSettingsInput;
